import { isAxiosError } from 'axios';

export class CustomError extends Error {
  code?: number;

  constructor(message: string, code?: number) {
    super(message);
    this.code = code;
  }
}

export interface IErrorData {
  message: string;
  code?: number;
}

export interface IMeta {
  error: IError | string | null;
  datetime: number;
}

export interface IError {
  message: string;
  code: number;
}

export interface IGenericResponseRoot<T> {
  response: T | null;
  meta: IMeta;
}

// Код для отмененных запросов
export const CANCEL_CODE = 499;

// тайпгард для проверки если ошибка типа IError
const typeguardIError = (data: unknown): data is IError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as IError)?.message === 'string' &&
    typeof (data as IError)?.code === 'number'
  );
};
// тайпгард для проверки если ошибка типа строка или null
const typeguardErrorString = (data: unknown): data is { error: string } => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as { error: string }).error === 'string'
  );
};
// тайпгард для проверки общего объекта ответа от сервера
const typeguardGenericResponseRoot = <T>(
  data: unknown
): data is IGenericResponseRoot<T> => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'response' in data &&
    'meta' in data &&
    typeof (data as IGenericResponseRoot<T>).meta === 'object'
  );
};
// тайпгард на общую ошибку типа Error
const typeguardError = (data: unknown): data is Error => {
  return data instanceof Error;
};

const typeguardRegularError = (data: unknown): data is IErrorData => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof data.message === 'string'
  );
};

export const getErrorData = (error: unknown): IErrorData => {
  const defaultErrorObj: IErrorData = {
    message: 'Произошла необработанная ошибка'
  };

  if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
    return {
      message: 'Запрос был отменен',
      code: CANCEL_CODE
    };
  }

  if (typeof error !== 'object') {
    return defaultErrorObj;
  }

  if (error instanceof CustomError) {
    const basicErrorData: IErrorData = {
      message: error.message,
      code: error.code
    };
    return basicErrorData;
  }

  if (isAxiosError(error)) {
    // === Обработка 413 ===
    if (error.response?.status === 413) {
      return {
        message:
          'Файл не может быть загружен, так как превышает максимально допустимый размер.',
        code: 413
      };
    }
    const hasErrorObj = typeof error.response?.data?.meta === 'object';
    if (hasErrorObj) {
      const errorUnknownObj = error.response?.data?.meta?.error;
      const metaErrorObj = error.response?.data?.meta;
      if (typeguardIError(errorUnknownObj)) {
        const errorObjData: IErrorData = {
          message: errorUnknownObj.message,
          code: errorUnknownObj.code
        };
        return errorObjData;
      }

      if (typeguardErrorString(metaErrorObj)) {
        const errorObjWithMessage: IErrorData = {
          message: metaErrorObj.error
        };
        return errorObjWithMessage;
      }
    }

    return defaultErrorObj;
  }

  if (typeguardGenericResponseRoot(error)) {
    if (typeguardIError(error.meta.error)) {
      const genericReponseErrorData: IErrorData = {
        message: error.meta.error.message,
        code: error.meta.error.code
      };

      return genericReponseErrorData;
    }

    if (typeguardErrorString(error.meta)) {
      const genericReponseErrorData: IErrorData = {
        message: error.meta.error
      };

      return genericReponseErrorData;
    }
  }

  if (typeguardError(error)) {
    const errorObjFromError: IErrorData = {
      message: error.message
    };

    return errorObjFromError;
  }

  if (typeguardRegularError(error)) {
    return error;
  }

  return defaultErrorObj;
};
